"use client";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import dynamic from "next/dynamic";
import { getImageUrlNew, getLink } from "../lib/util";
import { useDictionary } from "../context/dictionaryProvider";
import { trackEvent } from "../lib/engageXClient";

const DownloadBrochure = dynamic(() => import("./modal/DownloadBrochure"));
const StarRating = dynamic(() => import("./StarRating"));

const CourseCard = ({ data, coursePathPrefix = "" }) => {
  const dict = useDictionary();
  const params = useParams();
  const rating = 4.5;



  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
      {/* Image Section */}
      <div className="relative h-[220px] w-full flex-shrink-0">
        {data?.thumbnail ? (
          <Image
            src={getImageUrlNew(data?.thumbnail, "small")}
            alt={data?.course_name || ""}
            className="w-full h-full object-cover rounded-t-lg"
            fill
            loading="lazy"
          />
        ) : (
          <Image
            src="https://d3v381xb6gm5e.cloudfront.net/media/13969942_de24873a03.png"
            alt=""
            className="w-full h-full object-contain bg-gray-100 p-12 rounded-t-lg"
            fill
            loading="lazy"
          />
        )}
        
        {/* Category Badge - Only show if exists */}
        {data?.category?.name && (
          <div className="absolute top-3 left-3 bg-white text-gray-900 px-3 py-1 rounded text-sm font-medium shadow-md">
            {data?.category?.name}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5 flex flex-col flex-grow">
        {/* Course Title - Only show if exists */}
        {data?.course_name && (
          <Link
            prefetch={false}
            href={getLink(`/${params?.lang}${coursePathPrefix}/${data?.slug}`)}
            onClick={() =>
              trackEvent("course_clicked", {
                course: data?.course_name,
                category: data?.category?.name,
                // Only used on category pages (DemandCoursesGrid).
                source: "category_grid",
              })
            }
            className="block mb-3"
          >
            <h3 className="text-xl font-bold text-gray-900 hover:text-purple-600 transition-colors h-[60px] line-clamp-2 leading-[30px]">
              {data?.course_name}
            </h3>
          </Link>
        )}

        {/* Affiliations - Only show if exists */}
        {data?.affiliations?.data?.length > 0 && (
          <div className="flex items-center gap-2 text-sm mb-3">
            {dict?.courses?.accreditedBy && (
              <span className="text-gray-600">{dict?.courses?.accreditedBy}</span>
            )}
            <div className="flex items-center gap-3">
              {data?.affiliations?.data?.slice(0, 2).map((aff, index) => (
                <div key={index} className="relative h-[29px] w-[36px]">
                  <Image
                    loading="lazy"
                    fill
                    alt=""
                    src={getImageUrlNew(aff.logo, "thumbnail")}
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Short Description - Only show if exists */}
        {data?.short_description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-grow">
            {data?.short_description}
          </p>
        )}

        {/* Rating - Only show if exists */}
        {data?.rating && (
          <div className="flex items-center gap-2 mb-4">
            <StarRating rating={rating} />
            <p className="text-sm font-semibold text-gray-700">{data?.rating}/5</p>
          </div>
        )}

        {/* Download Brochure Button - Only show if exists */}
        {data?.brochure?.url && (
          <div className="mt-auto">
            <DownloadBrochure
              courseId={data?.id}
              document={data?.brochure}
              type="grid"
              location="category"
              course={data}
              submitEventName="Download brochure Grid course"
              closeEventName="Download brochure Grid course X"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default function DemandGrid({ courses = [], coursePathPrefix = "" }) {
  if (!courses || courses.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
      {courses.map((course, index) => (
        <CourseCard
          key={course?.id || index}
          data={course}
          coursePathPrefix={coursePathPrefix}
        />
      ))}
    </div>
  );
}
